import {
  addEffectsToScene,
  addEffectToScene,
  addTextbox,
  addTextTemplate,
  deleteEffectsFromScene,
  duplicateEffects,
  getEffects,
  updateEffectParams
} from 'modules/effects';
import { getSceneByTimeline } from 'modules/scenes';
import { nanoid } from 'nanoid/non-secure';
import { TextTemplateItem } from 'api';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import { setCurrentTextId } from 'modules/currentTextId';
import { getType } from 'deox';
import { FontStyleTypeEnum } from '@picsart/rc/types/containerComponents/Font';
import { Effect, Effects, Font, TextEffect } from 'types/Effects';
import { getKonvaTextSizeByEffect, getLines, getScale, getTextMaxWidth } from 'utils/konva';
import { call } from 'redux-saga-test-plan/matchers';
import { loadFont } from 'utils/textTool';
import { addAlert } from 'modules/alert';
import { getCanvasSize } from 'modules/canvas';
import { setCurrentWorkspace } from 'modules/workspace';
import { Workspace } from 'types/Clipboard';
import { getRatioFormatObject } from 'modules/ratioFormat';
import { BASE_SIZE, BASE_SIZE_FONT } from 'constants/canvas';
import { getTextTemplate } from 'utils/apiWrapper';
import { TEXT_DUPLICATE_STEP } from 'constants/textTool';
import { effectMessages } from 'constants/errorMessages';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';
import { SizeType } from 'types/Canvas';
import { RatioFormatObject } from 'types/RatioFormat';
import { hexToRgb } from '../../components/toolPanel/utils';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';
import Konva from 'konva';
import { getPlayer } from 'utils/PlayerContext';
import ErrorLogger from '../../components/ErrorLogger';

const getTemplateHeight = (effects: Effect[]) =>
  effects.length > 1
    ? effects[effects.length - 1].position.y +
      effects[effects.length - 1].height -
      effects[0].position.y
    : effects[0].height;
export function* loadEffectsFont(effects: Effect[]) {
  const fontToLoad = effects
    .filter((effect: Effect) => effect.name === 'text')
    .map((effect: Effect) => ({
      fontFamily: effect.font.fontFamily,
      src: effect.font.src
    }));

  yield all(fontToLoad.map((item: Font) => call(loadFont, item)));
}

export function setEffectTextSize(effects: Effect[]) {
  return effects.map(item =>
    item.name !== 'text'
      ? item
      : {
          ...item,
          width: getTextMaxWidth(item.textArr, item.font.fontFamily, item.fontSize),
          height: item.textArr.length * item.fontSize,
          // @ts-ignore TODO fix textColor in templates JSON
          textColor: hexToRgb(item.textColor)
        }
  );
}

export function setTextArr(effects: Effect[]) {
  return effects.map(item => {
    const textArr = getLines(item);
    return {
      ...item,
      textArr,
      height: textArr.length * item.fontSize
    };
  });
}

const getProportionCoef = (name: string) => {
  switch (name) {
    case 'addBodyText':
      return 0.5;
    case 'addSubHeading':
      return 0.8;
    case 'addStyledText':
      return 2;
    default:
      return 1;
  }
};

export function* duplicateEffectsSaga({
  payload: { effectIds, sceneId }
}: ReturnType<typeof duplicateEffects>) {
  const allEffects: Effects = yield select(getEffects);
  const effects: Effect[] = [];

  allEffects[sceneId]?.forEach((item: Effect) => {
    if (effectIds.includes(item.id)) {
      effects.push({
        ...item,
        id: nanoid(),
        position: {
          x: item.position.x + TEXT_DUPLICATE_STEP,
          y: item.position.y + TEXT_DUPLICATE_STEP
        }
      });
    }
  });

  yield put(addEffectsToScene(effects, sceneId));

  yield put(setCurrentTextId(effects.map(effect => effect.id)));
  yield put(saveRevision(ToolName.Text));
}

export function* addTextTemplateSaga({ payload }: ReturnType<typeof addTextTemplate>) {
  const player = getPlayer();
  if (player.playing) {
    return;
  }
  try {
    const templateId = payload._id || payload.id;
    const template: TextTemplateItem = yield call(getTextTemplate, templateId);
    if (!template || !template.data || !template.data.content) return;

    const { id: sceneId, scalingMode } = yield select(getSceneByTimeline(player));
    const canvasSize: SizeType = yield select(getCanvasSize);
    const scale = getScale(scalingMode, canvasSize.width, canvasSize.height);
    const ratioFormatObject: RatioFormatObject = yield select(getRatioFormatObject);

    yield call(
      loadEffectsFont,
      template.data.content.map(({ attrs: { fontFamily, font_resource } }: any) => ({
        name: 'text',
        font: {
          fontFamily,
          src: font_resource.resource_url
        }
      }))
    );

    let effects: Effect[] = template.data.content
      .sort((a: any, b: any) => a.attrs.y - b.attrs.y)
      .map(
        ({
          attrs: {
            text: value,
            fontFamily,
            font_resource,
            fontSize: templateFontSize,
            scaleX,
            y,
            fill,
            width,
            rotation,
            textAlign
          }
        }: any) => {
          const text = value.replace(/\n/g, '');
          // Make it a little bigger if ratio >= 1
          const scaleSize = ratioFormatObject.ratio >= 1 ? 1 : 0.5;
          const rWidth = (width * scaleX * scaleSize) / scale;
          const fontSize = (templateFontSize * scaleX * scaleSize) / scale;
          const color = { ...hexToRgb(fill), a: 1 };
          const effect: Effect = {
            id: nanoid(),
            name: 'text',
            text,
            textArr: [],
            height: 0,
            font: {
              fontFamily,
              src: font_resource.resource_url,
              isPaid: true
            },
            fontSize,
            position: {
              x: canvasSize.width / (scale * 2) - rWidth / 2,
              y: (y * scaleSize) / scale
            },
            background: '#00000000',
            textColor: color,
            width: rWidth,
            settings: [] as FontStyleTypeEnum[],
            rotation: rotation || 0,
            presetId: templateId,
            textAlign,
            outlineColor: color
          };

          effect.textArr = getLines(effect);

          return effect;
        }
      );

    effects.forEach(effect => {
      const text = new Konva.Text(effectToKonvaTextSettings(effect));
      effect.height = text.height();
    });

    const maxHeight = getTemplateHeight(effects) * scale;
    const heightScale = maxHeight > canvasSize.height ? (canvasSize.height * 0.8) / maxHeight : 0;

    effects = effects.map((item: Effect) => {
      const deltaY = item.position.y - effects[0].position.y - maxHeight / (2 * scale);
      const y = canvasSize.height / (scale * 2) + (heightScale ? deltaY * heightScale : deltaY);
      const fontSize = heightScale ? item.fontSize * heightScale : item.fontSize;
      const width = heightScale
        ? getTextMaxWidth(item.textArr, item.font.fontFamily, fontSize)
        : item.width;
      const x = heightScale ? canvasSize.width / (scale * 2) - width / 2 : item.position.x;
      return {
        ...item,
        position: {
          x,
          y
        },
        fontSize,
        height: item.textArr.length * fontSize,
        width
      };
    });

    yield all(effects.map((effect: Effect) => put(addEffectToScene(effect, sceneId))));
    yield put(setCurrentTextId(effects.map(({ id }: Effect) => id)));
    yield put(saveRevision(ToolName.TextPreset));
  } catch (e) {
    ErrorLogger.trigger(e);
    yield put(addAlert(effectMessages.fontLoadingTitle, effectMessages.fontLoading));
  }
}

export function* addTextboxSaga({
  payload: { text, name, fontItem, font_bold, textSettings }
}: ReturnType<typeof addTextbox>) {
  const player = getPlayer();
  const playing: boolean = player.playing;
  if (playing) {
    return;
  }
  const { id: sceneId } = yield select(getSceneByTimeline(player));
  const ratioFormat: RatioFormatObject = yield select(getRatioFormatObject);
  const id = nanoid();
  const textArr = [text];

  const height = BASE_SIZE;
  const width = BASE_SIZE * ratioFormat.ratio;

  const fontFamily = fontItem ? fontItem.meta_data.font_name : 'Montserrat';
  const fontSize = BASE_SIZE_FONT * ratioFormat.ratio * getProportionCoef(name);
  const font = fontItem
    ? {
        fontFamily: fontItem.meta_data.font_name,
        src: fontItem.data_url || fontItem.url,
        isPaid: fontItem.isPremium
      }
    : {
        fontFamily,
        src: 'https://pastatic.picsart.com/45080341663834066390.ttf', // ffmpeg support only ttf
        isPaid: false
      };

  yield loadFont(font);

  const effect: TextEffect = {
    id,
    name: 'text',
    text,
    textArr,
    fontSize,
    font,
    background: '#00000000',
    textColor: (textSettings?.fill_color && hexToRgb(textSettings?.fill_color)) || {
      r: 255,
      g: 255,
      b: 255,
      a: 1
    },
    outlineColor: (textSettings?.stroke_color && hexToRgb(textSettings?.stroke_color)) || {
      r: 255,
      g: 255,
      b: 255,
      a: 1
    },
    shadowColor: (textSettings?.shadow_color && hexToRgb(textSettings?.shadow_color)) || {
      r: 0,
      g: 0,
      b: 0,
      a: 1
    },
    isBold: !!font_bold,
    width: null,
    height: null,
    settings: [] as FontStyleTypeEnum[],
    position: { x: 0, y: 0 },
    outlineSize: textSettings?.stroke_width / 100 || 0,
    outlineOpacity: 1,
    shadowOpacity: textSettings?.shadow_opacity / 100 || 1,
    shadowBlur: textSettings?.shadow_blur || 0,
    letter: textSettings?.letter_spacing / 10 || null,
    line: textSettings?.line_spacing / 20 || null,
    textOpacity: textSettings?.opacity / 100 || 1,
    shadowOffsetX: textSettings?.shadow_offset_x || 0,
    shadowOffsetY: textSettings?.shadow_offset_y || 0
  };

  const size = getKonvaTextSizeByEffect(effect);

  effect.width = size.width + 4;
  effect.height = size.height;

  effect.position = {
    x: width / 2 - size.width / 2,
    y: height / 2 - fontSize / 2
  };

  yield put(addEffectToScene(effect, sceneId));
  yield put(setCurrentTextId([id]));
  yield put(setCurrentWorkspace(Workspace.Artboard));
  yield put(saveRevision(ToolName.Text));
}

function* updateEffectParamsSaga({
  payload: {
    sceneId,
    effectId,
    effectParams: { text }
  }
}: ReturnType<typeof updateEffectParams>) {
  if (text === '') {
    yield put(deleteEffectsFromScene([effectId], sceneId));
  }
}

export default function* effectsSaga() {
  yield takeEvery(getType(addTextbox), addTextboxSaga);
  yield takeEvery(getType(addTextTemplate), addTextTemplateSaga);
  yield takeEvery(getType(duplicateEffects), duplicateEffectsSaga);
  yield takeEvery(getType(updateEffectParams), updateEffectParamsSaga);
}
